// Fuente de la flota de Control MDM: `GET /backoffice/devices`, un stream por `mdmStatus`, cada uno
// con su cursor. Se piden en paralelo y el filtro «Estado MDM» decide cuáles.
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import { mdmBackofficeApi } from '@/api/services/loan-credit';
import { ApiError, userMessageOf } from '@/api/errors';
import { FLEET_STREAM_STATUSES, fleetStreamStatuses } from '../fleetStreams';
import type { MdmFleetState } from '../logic';
import type { AnyFilter, MdmFleetDevice } from '../types';
import { toFleetDevice } from './mappers';
import { MDM_FLEET_KEY } from './queryKeys';

/** Página del servidor. Mayor que la de la tabla para no pedir dos veces por página pintada. */
const SERVER_PAGE_SIZE = 50;

const STALE_TIME_MS = 30_000;

export type MdmFleetSourceState =
    | { status: 'loading' }
    // El error crudo viaja además del mensaje: quien lo pinta puede distinguir un 403 de una caída.
    | { status: 'error'; message: string; error: Error }
    | {
          status: 'ready';
          devices: readonly MdmFleetDevice[];
          /** Los `mdmStatus` pedidos: con el filtro puesto, la flota que se leyó no es la entera. */
          statuses: readonly LoanDeviceMdmStatusEnum[];
          /**
           * Los estados cuyo stream no trajo NADA: no falta una página suya, falta el estado entero.
           * Quien cuenta no puede afirmar un cero sobre ellos.
           */
          failedStatuses: readonly LoanDeviceMdmStatusEnum[];
          /** `true` = lo cargado NO es todo lo pedido; los conteos lo tienen que decir. */
          hasNextPage: boolean;
          fetchNextPage: () => void;
          isFetchingNextPage: boolean;
          /** Por qué falló la SIGUIENTE página de un stream que sí trajo equipos. `null` = no falló. */
          nextPageError: string | null;
      };

const LOADING: MdmFleetSourceState = { status: 'loading' };

/** La clave de un stream: el prefijo común deja que las acciones invaliden todos juntos. */
export function fleetStreamKey(status: LoanDeviceMdmStatusEnum): readonly unknown[] {
    return [...MDM_FLEET_KEY, 'status', status];
}

function isInvalidNextToken(error: unknown): boolean {
    return error instanceof ApiError && error.code === 'INVALID_NEXT_TOKEN';
}

/** UN stream: un `mdmStatus` con su cursor. */
function useFleetStream(status: LoanDeviceMdmStatusEnum, enabled: boolean) {
    const queryClient = useQueryClient();
    const queryKey = useMemo(() => fleetStreamKey(status), [status]);
    const stream = useInfiniteQuery({
        queryKey,
        queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
            mdmBackofficeApi.listMdmDevices({
                status,
                limit: SERVER_PAGE_SIZE,
                ...(pageParam === undefined ? {} : { nextToken: pageParam }),
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken,
        enabled,
        staleTime: STALE_TIME_MS,
        retry: false,
        // Volver a la pestaña relanzaría TODAS las páginas acumuladas, una por una.
        refetchOnWindowFocus: false,
    });

    // Un cursor que el silo ya no acepta reinicia el stream desde la primera página.
    const cursorRejected = isInvalidNextToken(stream.error);
    useEffect(() => {
        if (!cursorRejected) return;
        void queryClient.resetQueries({ queryKey, exact: true });
    }, [cursorRejected, queryKey, queryClient]);

    return stream;
}

/**
 * Lo que un stream APORTA a la lista, aparte de sus banderas: cambia solo cuando llegan equipos,
 * nunca al arrancar o terminar un fetch. De ahí sale `devices` sin reiniciar la página de la tabla.
 */
interface StreamData {
    status: LoanDeviceMdmStatusEnum;
    devices: readonly MdmFleetDevice[];
    /** Cuándo llegó su última respuesta; desempata copias repetidas sin `updatedAt`. */
    respondedAt: number;
}

/** Lo que el estado de la fuente lee de un stream: sus equipos y en qué anda. */
interface StreamSummary {
    data: StreamData;
    loaded: boolean;
    error: Error | null;
    hasNextPage: boolean;
    isFetching: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => unknown;
    refetch: () => unknown;
}

/** Un stream con sus equipos ya traducidos; memoizado para no re-renderizar la tabla en falso. */
function useStreamSummary(
    own: LoanDeviceMdmStatusEnum,
    wanted: readonly LoanDeviceMdmStatusEnum[],
    enabled: boolean,
): StreamSummary {
    const {
        data,
        dataUpdatedAt,
        error,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        refetch,
    } = useFleetStream(own, enabled && wanted.includes(own));
    const devices = useMemo(
        () => data?.pages.flatMap((page) => page.items.map(toFleetDevice)) ?? [],
        [data],
    );
    // Sin `isFetching` entre las dependencias: un fetch que arranca no trae equipos nuevos.
    const streamData = useMemo(
        () => ({ status: own, devices, respondedAt: dataUpdatedAt }),
        [own, devices, dataUpdatedAt],
    );
    return useMemo(
        () => ({
            data: streamData,
            loaded: data !== undefined,
            error,
            hasNextPage,
            isFetching,
            isFetchingNextPage,
            fetchNextPage,
            refetch,
        }),
        [
            streamData,
            data,
            error,
            hasNextPage,
            isFetching,
            isFetchingNextPage,
            fetchNextPage,
            refetch,
        ],
    );
}

/** `true` si `candidate` es más nueva: por `updatedAt`; sin él, por la respuesta más reciente. */
function isNewer(
    candidate: { device: MdmFleetDevice; respondedAt: number },
    current: { device: MdmFleetDevice; respondedAt: number },
): boolean {
    const candidateAt = candidate.device.updatedAt;
    const currentAt = current.device.updatedAt;
    if (candidateAt !== undefined && currentAt !== undefined && candidateAt !== currentAt) {
        return candidateAt > currentAt;
    }
    return candidate.respondedAt >= current.respondedAt;
}

/** Un equipo por crédito: un cambio de estado entre lecturas lo deja en dos streams o dos páginas. */
function latestByCredit(streams: readonly StreamData[]): MdmFleetDevice[] {
    const latest = new Map<string, { device: MdmFleetDevice; respondedAt: number }>();
    for (const stream of streams) {
        for (const device of stream.devices) {
            const candidate = { device, respondedAt: stream.respondedAt };
            const current = latest.get(device.creditId);
            if (current === undefined || isNewer(candidate, current)) {
                latest.set(device.creditId, candidate);
            }
        }
    }
    return [...latest.values()].map((entry) => entry.device);
}

/**
 * La flota acumulando páginas, un stream por `mdmStatus`. `enabled` en `false` no dispara nada: sin
 * el permiso de MDM el listado responde 403 y pedirlo solo gasta viajes.
 */
export function useMdmFleetSource(
    state: MdmFleetState | AnyFilter,
    enabled: boolean,
): MdmFleetSourceState {
    const wanted = useMemo(() => fleetStreamStatuses(state), [state]);

    // Siempre los mismos nueve hooks; el que el filtro no pide queda apagado con su caché intacta.
    const s0 = useStreamSummary(FLEET_STREAM_STATUSES[0], wanted, enabled);
    const s1 = useStreamSummary(FLEET_STREAM_STATUSES[1], wanted, enabled);
    const s2 = useStreamSummary(FLEET_STREAM_STATUSES[2], wanted, enabled);
    const s3 = useStreamSummary(FLEET_STREAM_STATUSES[3], wanted, enabled);
    const s4 = useStreamSummary(FLEET_STREAM_STATUSES[4], wanted, enabled);
    const s5 = useStreamSummary(FLEET_STREAM_STATUSES[5], wanted, enabled);
    const s6 = useStreamSummary(FLEET_STREAM_STATUSES[6], wanted, enabled);
    const s7 = useStreamSummary(FLEET_STREAM_STATUSES[7], wanted, enabled);
    const s8 = useStreamSummary(FLEET_STREAM_STATUSES[8], wanted, enabled);

    const streams = useMemo(
        () =>
            [s0, s1, s2, s3, s4, s5, s6, s7, s8].filter((stream) =>
                wanted.includes(stream.data.status),
            ),
        [s0, s1, s2, s3, s4, s5, s6, s7, s8, wanted],
    );

    // Se arma de los DATOS de cada stream, no de `streams`: cambiar `isFetching` no debe dar
    // equipos nuevos, o la tabla vuelve a la página 1 en cada transición de fetch.
    const data = useMemo(
        () =>
            [
                s0.data,
                s1.data,
                s2.data,
                s3.data,
                s4.data,
                s5.data,
                s6.data,
                s7.data,
                s8.data,
            ].filter((stream) => wanted.includes(stream.status)),
        [s0.data, s1.data, s2.data, s3.data, s4.data, s5.data, s6.data, s7.data, s8.data, wanted],
    );
    const devices = useMemo(() => latestByCredit(data), [data]);

    return useMemo(() => {
        // Resuelto = trajo datos O falló: el fallo de uno no deja la pantalla cargando para siempre.
        if (!streams.every((stream) => stream.loaded || stream.error)) return LOADING;

        const failing = streams.find((stream) => stream.error);
        // El error solo vacía la pantalla cuando no llegó NADA.
        if (!streams.some((stream) => stream.loaded)) {
            const error = failing?.error ?? new Error('Sin respuesta de la flota');
            return { status: 'error', message: userMessageOf(error), error };
        }

        // Nunca contestó = su estado falta entero. Ya cargado y caído = falló traer MÁS. Son avisos
        // distintos: el primero nombra el estado, el segundo habla de la siguiente página.
        const absent = streams.filter((stream) => stream.error !== null && !stream.loaded);
        const pagingFailure = streams.find((stream) => stream.error !== null && stream.loaded);

        // Caído sin cursor (primera página o refresco): react-query lo da por agotado y se repite entero.
        const needsRefetch = (stream: StreamSummary): boolean =>
            stream.error !== null && (!stream.loaded || !stream.hasNextPage);
        return {
            status: 'ready',
            devices,
            statuses: wanted,
            failedStatuses: absent.map((stream) => stream.data.status),
            hasNextPage: streams.some((stream) => stream.hasNextPage || needsRefetch(stream)),
            fetchNextPage: () => {
                for (const stream of streams) {
                    if (needsRefetch(stream)) void stream.refetch();
                    else if (stream.hasNextPage) void stream.fetchNextPage();
                }
            },
            isFetchingNextPage: streams.some(
                (stream) => stream.isFetchingNextPage || (!stream.loaded && stream.isFetching),
            ),
            nextPageError: pagingFailure?.error ? userMessageOf(pagingFailure.error) : null,
        };
    }, [streams, devices, wanted]);
}

/** ¿Hay equipos que contar? Con `false`, los KPIs muestran «—» y nunca un `0`. */
export function hasFleetDevices(
    state: MdmFleetSourceState,
): state is Extract<MdmFleetSourceState, { status: 'ready' }> {
    return state.status === 'ready';
}
